import styled from '@emotion/styled'
import { useEffect, useRef } from 'react'

import { formControlReset } from 'styles/reset'

type Props = {
	options: string[]
	selectedIndex: number
	onChange: (index: number) => void
}

const BackgroundBox = styled.div`
	position: relative;
	display: inline-block;
	padding: ${({ theme }) => theme.spaces.space2};
	background-color: ${({ theme }) => theme.colors.light.surfaceDecorative};
	border-radius: ${({ theme }) => theme.borderRadiuses.lg};
	display: flex;
	justify-content: space-around;
`

const Option = styled.button`
	${formControlReset};
	color: inherit;
	cursor: pointer;
	position: relative;
	z-index: ${({ theme }) => theme.zIndexes.base};
	display: inline-block;
	${({ theme }) => theme.typography.caption}
	padding: ${({ theme }) => `${theme.spaces.space2} ${theme.spaces.space6}`};
	width: 100%;
	white-space: nowrap;
`

const Toggle = styled.div`
	top: 4px;
	bottom: 4px;
	position: absolute;
	border-radius: ${({ theme }) => theme.borderRadiuses.md};
	background-color: ${({ theme }) => theme.colors.light.surface};
	box-shadow: ${({ theme }) => theme.boxShadows.xs};

	will-change: width, left;
	transition: width 200ms ${({ theme }) => theme.transitions.easings.css.spring};
	transition: left 200ms ${({ theme }) => theme.transitions.easings.css.swift};
`

function TextSwitch({ options, selectedIndex, onChange }: Props) {
	const toggleRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!toggleRef.current) {
			return
		}

		const option = toggleRef.current.parentElement?.children[selectedIndex] as HTMLButtonElement

		if (option?.nodeName !== 'BUTTON') {
			return
		}

		toggleRef.current.style.left = `${option.offsetLeft}px`
		toggleRef.current.style.width = `${option.clientWidth}px`
	}, [selectedIndex])

	return (
		<BackgroundBox>
			{options.map((option, index) => (
				<Option type="button" key={index} onClick={() => onChange(index)}>
					{option}
				</Option>
			))}
			<Toggle ref={toggleRef} />
		</BackgroundBox>
	)
}

export default TextSwitch
